import type { Tournament } from "@/entities/tournament/model/types";
import { translateUi } from "@/shared/lib/ui-translate";

/**
 * Все фазы турнира в порядке жизненного цикла.
 *
 * Единственный источник и для типа, и для проверки словаря
 * (`src/i18n/__tests__/messages-usage.test.ts`): фаза, которой нет в словаре фаз ассистента,
 * выводилась на экран сырым кодом (`draft_in_progress`) — ассистент показывает фазу
 * через `t(\`phases.${phase}\`)`. Добавили фазу — тест потребует подпись в ru и en.
 */
export const TOURNAMENT_PHASES = [
  "draft",
  "upcoming",
  "registration_open",
  "registration_closed",
  /** KAN-359: идут пики драфта. Команд и матчей ещё нет. */
  "draft_in_progress",
  "awaiting_bracket",
  "in_progress",
  "completed",
  "cancelled",
  "unknown",
] as const;

export type TournamentPhase = (typeof TOURNAMENT_PHASES)[number];

export type TournamentNextActionId =
  | "publish"
  | "open_registration"
  | "close_registration"
  | "open_draft_lobby"
  | "manage_draft"
  | "generate_bracket"
  | "start_tournament"
  /** KAN-1644: Arena стартует серией игр (POST /arena/.../start-series), а не сеткой. */
  | "start_series"
  | "manage_matches"
  | "complete_tournament"
  | null;

export type TournamentBlocker = {
  id: string;
  text: string;
  tab?: "overview" | "teams" | "bracket" | "schedule" | "management" | "settings";
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export function getTournamentStatusKey(
  tournament: Tournament | null | undefined,
): string {
  if (!tournament) return "";
  const backendStatus =
    isRecord(tournament) && typeof tournament.backendStatus === "string"
      ? tournament.backendStatus
      : undefined;
  return String(
    backendStatus ?? tournament.backendStatus ?? tournament.status ?? "",
  )
    .trim()
    .toLowerCase();
}

export function getTournamentPhase(
  tournament: Tournament | null | undefined,
): TournamentPhase {
  const s = getTournamentStatusKey(tournament);
  switch (s) {
    case "draft":
      return "draft";
    case "upcoming":
      return "upcoming";
    case "registrationopen":
      return "registration_open";
    case "registrationclosed":
      return "registration_closed";
    case "draftinprogress":
      return "draft_in_progress";
    case "awaitingbracket":
      return "awaiting_bracket";
    case "inprogress":
    case "ongoing":
    case "active":
      return "in_progress";
    case "completed":
    case "finished":
      return "completed";
    case "cancelled":
      return "cancelled";
    default:
      return "unknown";
  }
}

function minRequiredParticipantsForBracket(): number {
  return 2;
}

/**
 * Драфт определяем по обоим полям: `type` приходит с бэкенда, `format` — то, чем
 * пользуется остальной фронт. Разойтись они не должны, но полагаться на одно поле
 * значит молча выключить весь этап драфта, если второе окажется единственным.
 */
export function isDraftTournament(
  tournament: Tournament | null | undefined,
): boolean {
  if (!tournament) return false;
  const type = isRecord(tournament) ? String(tournament["type"] ?? "") : "";
  return type.toLowerCase() === "draft" || tournament.format === "Draft";
}

/**
 * Открыто ли лобби драфта — с точки зрения бэкенда, а не колонки в БД.
 *
 * Колонку `DraftLobbyOpenedAtUtc` пишет только ручное открытие, но проверки
 * готовности капитанов (`DraftHub.SetReady`, `AutoStartDraftIfReadyAsync`,
 * `InitializeDraftAsync`) считают лобби открытым ещё и по наступлению
 * `DraftStartAt`. То есть после времени старта капитаны уже могут отмечаться
 * готовыми, а колонка так и осталась пустой. Смотреть только на неё значит
 * предлагать «открыть лобби», которое уже открыто.
 */
export function isDraftLobbyOpen(
  tournament: Tournament | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!tournament) return false;
  if (tournament.draftLobbyOpenedAtUtc) return true;
  const startAt = tournament.draftStartAt;
  if (!startAt) return false;
  const ms = Date.parse(
    // Бэкенд отдаёт UTC без суффикса — без него Date.parse трактует строку
    // как локальное время, и лобби «открывается» на три часа позже (МСК).
    /[zZ]|[+-]\d{2}:?\d{2}$/.test(startAt) ? startAt : `${startAt}Z`,
  );
  return Number.isFinite(ms) && now >= ms;
}

function isSoloParticipants(tournament: Tournament): boolean {
  return tournament.format === "Draft" || tournament.format === "SoloOneVsOne";
}

function getLocalBlockers(
  tournament: Tournament,
  action: TournamentNextActionId,
): TournamentBlocker[] {
  const blockers: TournamentBlocker[] = [];
  const registered = tournament.currentTeams || 0;
  const minRequired = minRequiredParticipantsForBracket();
  if (action === "close_registration" || action === "generate_bracket") {
    if (registered < minRequired) {
      blockers.push({
        id: "min_participants",
        text: translateUi(
          isSoloParticipants(tournament)
            ? "litTournamentLifecycle.nuzhnoMinimumUchastnikov"
            : "litTournamentLifecycle.nuzhnoMinimumKomand",
          { count: minRequired, registered },
        ),
        tab: "teams",
      });
    }
  }
  return blockers;
}

export function getTournamentNextAction(
  tournament: Tournament | null | undefined,
): {
  phase: TournamentPhase;
  action: TournamentNextActionId;
  label: string | null;
  canRun: boolean;
  blockers: TournamentBlocker[];
  disabledReason: string | null;
} {
  if (!tournament) {
    return {
      phase: "unknown",
      action: null,
      label: null,
      canRun: false,
      blockers: [],
      disabledReason: null,
    };
  }

  const phase = getTournamentPhase(tournament);
  const statusKey = getTournamentStatusKey(tournament);
  const canEdit = Boolean(tournament.capabilities?.canEditTournament);
  const disabledReasons = isRecord(tournament.capabilities)
    ? ((tournament.capabilities["disabledReasons"] as
        | Record<string, string>
        | null) ?? null)
    : null;

  if (statusKey === "draft") {
    const canRun = canEdit;
    const reason =
      !canRun ? (disabledReasons?.["editTournament"] ?? translateUi("litTournamentLifecycle.nedostatochnoPrav")) : null;
    return {
      phase,
      action: "publish",
      label: translateUi("litTournamentLifecycle.opublikovat"),
      canRun,
      blockers: canRun ? [] : [{ id: "no_rights", text: reason!, tab: "overview" }],
      disabledReason: canRun ? null : reason,
    };
  }

  if (statusKey === "upcoming") {
    const canRun = canEdit;
    const reason =
      !canRun ? (disabledReasons?.["editTournament"] ?? translateUi("litTournamentLifecycle.nedostatochnoPrav")) : null;
    return {
      phase,
      action: "open_registration",
      label: translateUi("litTournamentLifecycle.otkrytRegistratsiyu"),
      canRun,
      blockers: canRun ? [] : [{ id: "no_rights", text: reason!, tab: "overview" }],
      disabledReason: canRun ? null : reason,
    };
  }

  if (statusKey === "registrationopen") {
    const local = getLocalBlockers(tournament, "close_registration");
    const canRun = canEdit && local.length === 0;
    const reason = !canEdit
      ? (disabledReasons?.["editTournament"] ?? translateUi("litTournamentLifecycle.nedostatochnoPrav"))
      : local.length
        ? local[0]!.text
        : null;
    return {
      phase,
      action: "close_registration",
      label: translateUi("litTournamentLifecycle.zakrytRegistratsiyu"),
      canRun,
      blockers: canRun
        ? []
        : [
            ...(!canEdit
              ? [{ id: "no_rights", text: reason!, tab: "overview" as const }]
              : []),
            ...local,
          ],
      disabledReason: canRun ? null : reason,
    };
  }

  if (statusKey === "registrationclosed") {
    const hasBracket = isRecord(tournament) ? Boolean(tournament["hasBracket"]) : false;
    const isBracketPublished = isRecord(tournament) ? Boolean(tournament["isBracketPublished"]) : false;

    // Arena турниры НЕ имеют сетки (серия игр + лидерборд). Не предлагаем
    // «Сгенерировать сетку». Старт — это запуск серии (POST /arena/.../start-series):
    // обычный /start требует сгенерированную сетку и падает с 400 (KAN-1644).
    const isArena =
      isRecord(tournament) &&
      String(tournament["type"] ?? "").toLowerCase() === "arena";
    if (isArena) {
      const canRun = canEdit;
      const reason = !canRun
        ? (disabledReasons?.["editTournament"] ?? translateUi("litTournamentLifecycle.nedostatochnoPrav"))
        : null;
      return {
        phase,
        action: "start_series",
        label: translateUi("litTournamentLifecycle.zapustitSeriyu"),
        canRun,
        blockers: canRun ? [] : [{ id: "no_rights", text: reason!, tab: "overview" as const }],
        disabledReason: canRun ? null : reason,
      };
    }

    // KAN-358: у драфт-турнира между «регистрация закрыта» и сеткой стоит целый
    // этап — драфт. Команд до его завершения физически не существует, поэтому
    // «Сгенерировать сетку» первым шагом было и бесполезно, и дезориентировало:
    // центр управления молчал ровно там, где организатор не знает, что делать.
    if (isDraftTournament(tournament) && !tournament.isDraftCompleted) {
      const lobbyOpened = isDraftLobbyOpen(tournament);

      if (!lobbyOpened) {
        const reason = !canEdit
          ? (disabledReasons?.["editTournament"] ?? translateUi("litTournamentLifecycle.nedostatochnoPrav"))
          : null;
        return {
          phase,
          action: "open_draft_lobby",
          label: translateUi("litTournamentLifecycle.otkrytLobbiDrafta"),
          canRun: canEdit,
          blockers: canEdit
            ? []
            : [{ id: "no_rights", text: reason!, tab: "overview" as const }],
          disabledReason: reason,
        };
      }

      // Лобби открыто: дальше всё решают капитаны — драфт стартует сам, когда все
      // отметятся готовыми. Организатору тут нечего нажимать, только наблюдать.
      return {
        phase,
        action: "manage_draft",
        label: tournament.isDraftStarted
          ? translateUi("litTournamentLifecycle.pereytiVKomnatuDrafta")
          : translateUi("litTournamentLifecycle.otkrytKomnatuDrafta"),
        canRun: true,
        blockers: [],
        disabledReason: null,
      };
    }

    // Bracket exists and published → ready to start
    if (hasBracket && isBracketPublished) {
      const canRun = canEdit;
      const reason = !canRun
        ? (disabledReasons?.["editTournament"] ?? translateUi("litTournamentLifecycle.nedostatochnoPrav"))
        : null;
      return {
        phase,
        action: "start_tournament",
        label: translateUi("litTournamentLifecycle.nachatTurnir"),
        canRun,
        blockers: canRun ? [] : [{ id: "no_rights", text: reason!, tab: "overview" as const }],
        disabledReason: canRun ? null : reason,
      };
    }

    // Bracket exists but not published → no main action (publish button shown separately in TCC)
    if (hasBracket) {
      return {
        phase,
        action: null,
        label: null,
        canRun: false,
        blockers: [],
        disabledReason: null,
      };
    }

    // No bracket → generate
    const local = getLocalBlockers(tournament, "generate_bracket");
    const canGenerate = Boolean(tournament.capabilities?.canGenerateBracket);
    const canRun = canGenerate && local.length === 0;
    const reason =
      !canGenerate
        ? disabledReasons?.["generateBracket"] ?? translateUi("litTournamentLifecycle.nedostupno")
        : local.length
          ? local[0]!.text
          : null;
    const blockers = [
      ...(!canGenerate && reason
        ? [{ id: "disabled", text: reason, tab: "management" as const }]
        : []),
      ...local,
    ];
    return {
      phase,
      action: "generate_bracket",
      label: translateUi("litTournamentLifecycle.sgenerirovatSetku"),
      canRun,
      blockers: canRun ? [] : blockers,
      disabledReason: canRun ? null : reason,
    };
  }

  if (statusKey === "awaitingbracket") {
    const isBracketPublished = isRecord(tournament)
      ? Boolean(tournament["isBracketPublished"])
      : false;
    if (isBracketPublished) {
      const canRun = canEdit;
      const reason = !canRun
        ? (disabledReasons?.["editTournament"] ?? translateUi("litTournamentLifecycle.nedostatochnoPrav"))
        : null;
      return {
        phase,
        action: "start_tournament",
        label: translateUi("litTournamentLifecycle.nachatTurnir"),
        canRun,
        blockers: canRun ? [] : [{ id: "no_rights", text: reason!, tab: "overview" as const }],
        disabledReason: canRun ? null : reason,
      };
    }

    // KAN-358: после финализации драфта статус становится AwaitingBracket, а сетки
    // ещё нет — здесь возвращался `action: null`, то есть организатор оставался
    // ровно с тем же тупиком, ради которого задача и делалась: команды собраны,
    // а «Сгенерировать сетку» никто не предлагает.
    const local = getLocalBlockers(tournament, "generate_bracket");
    const canGenerate = Boolean(tournament.capabilities?.canGenerateBracket);
    const canRun = canGenerate && local.length === 0;
    const reason = !canGenerate
      ? (disabledReasons?.["generateBracket"] ?? translateUi("litTournamentLifecycle.nedostupno"))
      : local.length
        ? local[0]!.text
        : null;
    return {
      phase,
      action: "generate_bracket",
      label: translateUi("litTournamentLifecycle.sgenerirovatSetku"),
      canRun,
      blockers: canRun
        ? []
        : [
            ...(!canGenerate && reason
              ? [{ id: "disabled", text: reason, tab: "management" as const }]
              : []),
            ...local,
          ],
      disabledReason: canRun ? null : reason,
    };
  }

  // KAN-359: во время драфта единственное осмысленное место — комната драфта.
  // Ветка `inprogress` ниже сохранена: пока не выкачен бэкенд с новым статусом,
  // драфт по-прежнему приезжает как InProgress, и оба образа должны работать.
  if (statusKey === "draftinprogress") {
    return {
      phase,
      action: "manage_draft",
      label: translateUi("litTournamentLifecycle.pereytiVKomnatuDrafta"),
      canRun: true,
      blockers: [],
      disabledReason: null,
    };
  }

  if (statusKey === "inprogress" || statusKey === "ongoing" || statusKey === "active") {
    // KAN-358: старт драфта переводит турнир в InProgress, но матчей ещё нет —
    // «Управлять матчами» вело в редактор несуществующей сетки. Пока драфт идёт,
    // единственное осмысленное место — комната драфта.
    if (isDraftTournament(tournament) && !tournament.isDraftCompleted) {
      return {
        phase,
        action: "manage_draft",
        label: translateUi("litTournamentLifecycle.pereytiVKomnatuDrafta"),
        canRun: true,
        blockers: [],
        disabledReason: null,
      };
    }

    // Arena не имеет сетки/матчей — вместо «Управлять матчами» (тупиковый редактор
    // сетки) предлагаем завершить турнир: очки вбиваются в лидерборде, финал — это
    // перевод в Completed (→ пьедестал).
    const isArena =
      isRecord(tournament) &&
      String(tournament["type"] ?? "").toLowerCase() === "arena";
    if (isArena) {
      return {
        phase,
        action: "complete_tournament",
        label: translateUi("litTournamentLifecycle.zavershitTurnir"),
        canRun: true,
        blockers: [],
        disabledReason: null,
      };
    }
    return {
      phase,
      action: "manage_matches",
      label: translateUi("litTournamentLifecycle.upravlyatMatchami"),
      canRun: true,
      blockers: [],
      disabledReason: null,
    };
  }

  return {
    phase,
    action: null,
    label: null,
    canRun: false,
    blockers: [],
    disabledReason: null,
  };
}

